import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import api from '../../utils/api';
import { parseWebsiteUrl } from '../../utils/websiteUrl';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

/** Match backend normalizeDomainInput: hostname from URL or bare host (no path, no @). */
function domainHostFromWebsite(raw) {
  let s = String(raw || '').trim().toLowerCase();
  s = s.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];
  if (s.startsWith('@')) s = s.slice(1);
  if (s.startsWith('www.') && s.length > 4 && s.includes('.', 4)) {
    s = s.slice(4);
  }
  return s;
}

function suggestedHostnameForAccount(a) {
  if (!a || typeof a !== 'object') return '';
  const raw = a.website ?? a.web_site ?? '';
  let host = domainHostFromWebsite(raw);
  if (host && host.includes('.') && !host.includes('@')) return host;
  const { host: fromForm } = parseWebsiteUrl(raw);
  host = domainHostFromWebsite(fromForm);
  if (host && host.includes('.') && !host.includes('@')) return host;
  return '';
}

export default function AdminCompanyDomains({ embedded = false }) {
  const { user } = useAuth();
  const mfaRequired = Boolean(user?.otp_enabled && user?.otp_verified);

  const [rows, setRows] = useState([]);
  const [primaryPolicy, setPrimaryPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [domain, setDomain] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [formError, setFormError] = useState('');
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);

  const [credentialDialog, setCredentialDialog] = useState(null);
  const [credentialPassword, setCredentialPassword] = useState('');
  const [credentialOtp, setCredentialOtp] = useState('');
  const [credentialBusy, setCredentialBusy] = useState(false);

  const [requestDialog, setRequestDialog] = useState(null);
  const [requestReason, setRequestReason] = useState('');
  const [requestBusy, setRequestBusy] = useState(false);

  const applyPrimaryPolicy = useCallback((p) => {
    setPrimaryPolicy(p || null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/admin/company-domains');
      const payload = r.data?.data;
      if (Array.isArray(payload)) {
        setRows(payload);
        applyPrimaryPolicy(null);
      } else {
        setRows(payload?.domains ?? []);
        applyPrimaryPolicy(payload?.primaryPolicy ?? null);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load domains.');
      setRows([]);
      applyPrimaryPolicy(null);
    } finally {
      setLoading(false);
    }
  }, [applyPrimaryPolicy]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setAccountsLoading(true);
    setSelectedAccount(null);
    setDomain('');
    setIsPrimary(false);
    setFormError('');
    api
      .get('/admin/company-domain-accounts')
      .then((r) => {
        if (!cancelled) setAccounts(r.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const freezeActive = Boolean(primaryPolicy?.freezeActive);
  const approvalLocked = Boolean(primaryPolicy?.primaryChangeRequiresApproval);
  const pendingRequest = primaryPolicy?.pendingRequest || null;

  const mergePolicyFromResponse = (r) => {
    const p = r?.data?.data?.primaryPolicy;
    if (p) applyPrimaryPolicy(p);
  };

  const accountsForAddDomain = useMemo(() => {
    const byId = new Map();
    for (const a of accounts) {
      if (a?.id && !byId.has(a.id)) byId.set(a.id, a);
    }
    return [...byId.values()].filter((a) => String(a.account_type || '').toLowerCase() === 'hosting_company');
  }, [accounts]);

  const submit = async () => {
    setFormError('');
    if (!selectedAccount?.id) {
      setFormError('Select a hosting company account. Domains are only added for account type hosting_company.');
      return;
    }
    if (!domain.trim()) {
      setFormError('Enter a domain.');
      return;
    }
    if (isPrimary && freezeActive) {
      setFormError('Primary domain changes are frozen. Wait until the freeze expires or clear it with password/MFA.');
      return;
    }
    if (isPrimary && approvalLocked) {
      setFormError('Approval lock is on. Add the domain without primary, then submit a primary change request.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/admin/company-domains', {
        account_id: selectedAccount.id,
        domain: domain.trim(),
        is_primary: isPrimary,
      });
      const msg = r.data?.message;
      if (msg) toast.success(msg, { autoClose: 10000 });
      setOpen(false);
      await load();
    } catch (e) {
      const msg = e.response?.data?.message || 'Save failed.';
      const errs = e.response?.data?.errors;
      const first = Array.isArray(errs) && errs[0] ? errs[0].msg || errs[0].message : null;
      setFormError(first || msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (
      !window.confirm(
        'Remove this domain? For hosting companies, auto-created demo mailboxes for this hostname are removed first. You cannot remove the row while other users, contacts, user ID patterns, pending primary-domain requests, or employee provision links still reference this hostname (the server lists what is blocking).'
      )
    )
      return;
    try {
      await api.delete(`/admin/company-domains/${id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed.');
    }
  };

  const makePrimary = async (row) => {
    if (!row?.id) return;
    if (freezeActive) {
      setError('Primary domain changes are frozen for this tenant. No switches until the freeze ends (or an administrator clears it with password/MFA).');
      return;
    }
    if (approvalLocked) {
      setRequestReason('');
      setRequestDialog(row);
      return;
    }
    setSettingPrimaryId(row.id);
    setError('');
    try {
      await api.patch(`/admin/company-domains/${row.id}`, { is_primary: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not set primary domain.');
    } finally {
      setSettingPrimaryId(null);
    }
  };

  const submitPrimaryChangeRequest = async () => {
    if (!requestDialog?.id) return;
    const reason = String(requestReason || '').trim();
    if (reason.length < 10) {
      setError('Reason must be at least 10 characters.');
      return;
    }
    setRequestBusy(true);
    setError('');
    try {
      const r = await api.post('/admin/company-domains/primary-change-requests', {
        target_domain_id: requestDialog.id,
        reason,
      });
      mergePolicyFromResponse(r);
      setRequestDialog(null);
      setRequestReason('');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not submit request.');
    } finally {
      setRequestBusy(false);
    }
  };

  const runCredentialAction = async () => {
    if (!credentialDialog?.action) return;
    const password = String(credentialPassword || '');
    const otp = String(credentialOtp || '').trim().replace(/\s/g, '');
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (mfaRequired && !otp) {
      setError('Enter your authenticator code (MFA is enabled).');
      return;
    }
    const body = { password, ...(otp ? { otp } : {}) };
    const endpoints = {
      lock: '/admin/company-domains/primary-policy/require-approval',
      release: '/admin/company-domains/primary-policy/release-approval',
      freeze: '/admin/company-domains/primary-policy/freeze-week',
      clearFreeze: '/admin/company-domains/primary-policy/clear-freeze',
    };
    const url = endpoints[credentialDialog.action];
    if (!url) return;
    setCredentialBusy(true);
    setError('');
    try {
      const r = await api.post(url, body);
      mergePolicyFromResponse(r);
      setCredentialDialog(null);
      setCredentialPassword('');
      setCredentialOtp('');
      await load();
    } catch (e) {
      const msg = e.response?.data?.message || 'Action failed.';
      const code = e.response?.data?.errors?.code;
      if (code === 'MFA_REQUIRED') {
        setError('MFA code required. Enter your authenticator code and try again.');
      } else {
        setError(msg);
      }
    } finally {
      setCredentialBusy(false);
    }
  };

  const approveRequest = async () => {
    if (!pendingRequest?.id) return;
    try {
      const r = await api.post(`/admin/company-domains/primary-change-requests/${pendingRequest.id}/approve`);
      mergePolicyFromResponse(r);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Approve failed.');
    }
  };

  const rejectRequest = async () => {
    if (!pendingRequest?.id) return;
    try {
      const r = await api.post(`/admin/company-domains/primary-change-requests/${pendingRequest.id}/reject`);
      mergePolicyFromResponse(r);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Reject failed.');
    }
  };

  const freezeUntilLabel = primaryPolicy?.freezeUntil
    ? new Date(primaryPolicy.freezeUntil).toLocaleString()
    : '';

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Company domains
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each domain is tied to an <strong>Account</strong> (company) under Accounts. There is exactly{' '}
            <strong>one primary domain</strong> for the whole tenant at any time; all other domains are secondary.
          </Typography>
        </>
      )}
      {embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each domain is tied to a company <strong>Account</strong>. Exactly <strong>one primary domain</strong> applies per tenant.
          You can <strong>lock</strong> primary changes behind password/MFA and an approval workflow, or <strong>freeze</strong> all primary
          changes for 7 days.
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Primary domain protection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            <strong>Approval lock</strong> (password/MFA): switching primary is blocked until someone submits a <strong>reason</strong>; an
            administrator (same permissions as here) must <strong>approve</strong> the request.
            <br />
            <strong>7-day freeze</strong> (password/MFA): no primary switches and no approvals until the freeze expires — or use{' '}
            <strong>Clear freeze</strong> with password/MFA.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            {approvalLocked ? (
              <Chip color="warning" label="Approval lock ON" size="small" />
            ) : (
              <Chip label="Approval lock off" size="small" variant="outlined" />
            )}
            {freezeActive ? (
              <Chip color="error" label={`Frozen until ${freezeUntilLabel}`} size="small" />
            ) : (
              <Chip label="No active freeze" size="small" variant="outlined" />
            )}
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<LockIcon />}
              disabled={approvalLocked || freezeActive}
              onClick={() => {
                setCredentialPassword('');
                setCredentialOtp('');
                setCredentialDialog({ action: 'lock' });
              }}
            >
              Lock primary (approval required)
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LockOpenIcon />}
              disabled={!approvalLocked || freezeActive}
              onClick={() => {
                setCredentialPassword('');
                setCredentialOtp('');
                setCredentialDialog({ action: 'release' });
              }}
            >
              Unlock approval requirement
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<TimelapseIcon />}
              disabled={freezeActive}
              onClick={() => {
                setCredentialPassword('');
                setCredentialOtp('');
                setCredentialDialog({ action: 'freeze' });
              }}
            >
              Freeze primary changes (7 days)
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<AcUnitIcon />}
              disabled={!freezeActive}
              onClick={() => {
                setCredentialPassword('');
                setCredentialOtp('');
                setCredentialDialog({ action: 'clearFreeze' });
              }}
            >
              Clear freeze
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {pendingRequest ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="success" size="small" variant="contained" disabled={freezeActive} onClick={approveRequest}>
                Approve
              </Button>
              <Button color="inherit" size="small" variant="outlined" onClick={rejectRequest}>
                Reject
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={700}>
            Pending primary change
          </Typography>
          <Typography variant="body2">
            Requested by{' '}
            <strong>
              {[pendingRequest.requester?.first_name, pendingRequest.requester?.last_name].filter(Boolean).join(' ') ||
                pendingRequest.requester?.email ||
                '—'}
            </strong>
            {' · '}
            Target: <strong>{pendingRequest.targetDomain?.domain || '—'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Reason:</strong> {pendingRequest.reason}
          </Typography>
          {freezeActive ? (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Cannot approve while frozen.
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpen(true)}>
          Add domain
        </Button>
      </Stack>
      <Card>
        <CardContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account (company)</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Primary</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="center">Switch primary</TableCell>
                    <TableCell align="right">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No domains yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          {r.account ? (
                            <>
                              <Typography variant="body2" component="span">
                                {r.account.account_code} — {r.account.company_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Account ID: {r.account.id}
                              </Typography>
                            </>
                          ) : (
                            <>
                              {r.company_name}
                              <Chip size="small" label="No account link" sx={{ ml: 1, height: 22 }} variant="outlined" />
                            </>
                          )}
                        </TableCell>
                        <TableCell>{r.domain}</TableCell>
                        <TableCell>
                          {r.is_primary ? <Chip size="small" label="Primary" color="primary" /> : '—'}
                        </TableCell>
                        <TableCell>{r.is_active ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="center">
                          {!r.is_primary && r.is_active ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={settingPrimaryId === r.id || freezeActive}
                              onClick={() => makePrimary(r)}
                            >
                              {approvalLocked ? 'Request primary…' : settingPrimaryId === r.id ? 'Updating…' : 'Set as primary'}
                            </Button>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => remove(r.id)} aria-label="delete">
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(open)} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add company domain (hosting company)</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={accountsForAddDomain}
              loading={accountsLoading}
              value={selectedAccount}
              onChange={(_, v) => {
                setSelectedAccount(v);
                const suggested = suggestedHostnameForAccount(v);
                if (suggested) setDomain(suggested);
              }}
              getOptionLabel={(a) => (a ? `${a.account_code} — ${a.company_name}` : '')}
              isOptionEqualToValue={(a, b) => Boolean(a && b && a.id === b.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company account"
                  required
                  size="small"
                  helperText="Only hosting_company accounts can have new domains (sister concerns / tenants). Each hosting account can have several hostnames. When the account has a website, the domain field fills from it (edit as needed)."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {accountsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                accountsLoading
                  ? 'Loading…'
                  : accountsForAddDomain.length === 0
                    ? 'No active hosting_company accounts. Create one under Accounts (type hosting company), then add domains here.'
                    : 'No matches.'
              }
            />
            <TextField
              label="Domain"
              fullWidth
              size="small"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="erp4consulting.com"
              helperText="No @ or https:// — hostname only. Leading www. is dropped when you save (apex domain)."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  disabled={freezeActive || approvalLocked}
                />
              }
              label="Make this the tenant primary domain (clears primary from every other domain)"
            />
            {(freezeActive || approvalLocked) && (
              <Typography variant="caption" color="text.secondary">
                {freezeActive
                  ? 'Primary assignment is disabled while frozen.'
                  : 'Approval lock is on — add as secondary, then use Request primary on the row.'}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving || !selectedAccount || !domain.trim()}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(credentialDialog)} onClose={() => !credentialBusy && setCredentialDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {credentialDialog?.action === 'lock' && 'Confirm — lock primary (approval required)'}
          {credentialDialog?.action === 'release' && 'Confirm — unlock approval requirement'}
          {credentialDialog?.action === 'freeze' && 'Confirm — freeze primary changes (7 days)'}
          {credentialDialog?.action === 'clearFreeze' && 'Confirm — clear freeze'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your login password{mfaRequired ? ' and authenticator code' : ''} to continue.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              size="small"
              value={credentialPassword}
              onChange={(e) => setCredentialPassword(e.target.value)}
              autoComplete="current-password"
            />
            {mfaRequired ? (
              <TextField
                fullWidth
                label="Authenticator code"
                size="small"
                value={credentialOtp}
                onChange={(e) => setCredentialOtp(e.target.value)}
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !credentialBusy && setCredentialDialog(null)} disabled={credentialBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runCredentialAction} disabled={credentialBusy}>
            {credentialBusy ? <CircularProgress size={22} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(requestDialog)} onClose={() => !requestBusy && setRequestDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Request primary domain change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Primary is approval-locked. Provide a reason (min. 10 characters). An administrator must approve this request before the switch
            applies.
          </Typography>
          {requestDialog ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Target domain: <strong>{requestDialog.domain}</strong>
            </Typography>
          ) : null}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason for change"
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            placeholder="Explain why the primary domain should change…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !requestBusy && setRequestDialog(null)} disabled={requestBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitPrimaryChangeRequest} disabled={requestBusy}>
            {requestBusy ? <CircularProgress size={22} /> : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
